if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      abortController = undefined;
      onVariantChangeUnsubscriber = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();
        this.handleDynamicVariantInputChange = this.handleDynamicVariantInputChange.bind(this);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get productId() {
        return this.getAttribute('data-product-id');
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get quantityInput() {
        return this.querySelector('quantity-input input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = FoxTheme.pubsub.subscribe(
          FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.addEventListener('change', this.handleDynamicVariantInputChange);

        this.currentVariant = this.getSelectedVariant(this);
        if (this.currentVariant) {
          this.updateDynamicVariantContent(this.currentVariant);
          this.safeUpdateMedia(this.currentVariant);
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.removeEventListener('change', this.handleDynamicVariantInputChange);
      }

      initializeProductSwapUtility() {
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;
        const viewMode = this.dataset.viewMode || 'main-product';

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage, viewMode)
            : this.handleUpdateProductInfo(productUrl, viewMode),
        });
      }

      get productForm() {
        return this.querySelector('form[is="product-form"');
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.resetFormState();
      }

      handleSwapProduct(productUrl, updateFullPage, viewMode) {
        return (html) => {
          const quickView = html.querySelector('#MainProduct-quick-view__content');
          if (quickView && viewMode === 'quick-view') {
            html = quickView.content.cloneNode(true);
          }
          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));

          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;
            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
            HTMLUpdateUtility.viewTransition(
              document.getElementById('shopify-section-sticky-atc-bar'),
              html.getElementById('shopify-section-sticky-atc-bar'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }

          this.currentVariant = variant;
        };
      }

      handleUpdateProductInfo(productUrl, viewMode) {
        return (html) => {
          const quickView = html.querySelector('#MainProduct-quick-view__content');
          if (quickView && viewMode === 'quick-view') {
            html = quickView.content.cloneNode(true);
          }
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateShareUrl(variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.currentVariant = variant;
          this.updateDynamicVariantContent(variant);
          this.safeUpdateMedia(variant);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Barcode', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Badges', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('PricePerItem', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Volume');
          this.updateDynamicVariantContent(variant);

          this.updateQuantityRules(this.sectionId, this.productId, html);
          this.querySelector(`#QuantityRules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#VolumeNote-${this.dataset.section}`)?.classList.remove('hidden');

          HTMLUpdateUtility.viewTransition(
            document.querySelector(`#SizeChart-${this.sectionId}`),
            html.querySelector(`#SizeChart-${this.sectionId}`),
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );

          const addButtonUpdated = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
          this.toggleAddButton(
            addButtonUpdated ? addButtonUpdated.hasAttribute('disabled') : true,
            FoxTheme.variantStrings.soldOut
          );

          document.dispatchEvent(
            new CustomEvent('variant:changed', {
              detail: {
                variant: this.currentVariant,
              },
            })
          );

          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      updateURL(url, variantId) {
        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      updateVariantInputs(variantId) {
        document
          .querySelectorAll(`#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`)
          .forEach((productForm) => {
            const input = productForm.querySelector('input[name="id"]');
            input.value = variantId ?? '';
            input.dispatchEvent(new Event('change', { bubbles: true }));
          });
      }

      safeUpdateMedia(variant) {
        try {
          this.updateMedia(variant);
        } catch (error) {
          console.error('Product media update failed', error);
        }
      }

      updateMedia(variant) {
        const productMedia = this.querySelector(`[id^="MediaGallery-${this.dataset.section}"]`);
        if (!productMedia) return; // Early return if productMedia is not found

        const setActiveMedia = () => {
          if (typeof productMedia.setActiveMedia === 'function') {
            productMedia.init();
            productMedia.setActiveMedia(variant);
            return true; // Indicate success
          }
          return false; // Indicate failure
        };

        if (!setActiveMedia()) {
          this.timer = setInterval(() => {
            if (setActiveMedia()) {
              clearInterval(this.timer);
            }
          }, 100);
        }
      }

      updateShareUrl(variantId) {
        if (!variantId) return;
        const shareButton = document.getElementById(`ProductShare-${this.dataset.section}`);
        if (!shareButton || !shareButton.updateUrl) return;
        shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${variantId}`);
      }

      getOptionNames() {
        try {
          return JSON.parse(this.dataset.optionNames || '[]');
        } catch (error) {
          return [];
        }
      }

      getVariantOptions(variant) {
        const optionNames = this.getOptionNames();
        return optionNames
          .map((name, index) => ({
            name,
            value: variant?.options?.[index] || variant?.[`option${index + 1}`],
          }))
          .filter(({ name, value }) => name && value);
      }

      getProductVariants() {
        if (this.productVariants) return this.productVariants;

        try {
          this.productVariants = JSON.parse(
            this.querySelector('[data-dynamic-product-variants]')?.textContent || '[]'
          );
        } catch (error) {
          this.productVariants = [];
        }

        return this.productVariants;
      }

      getVariantFromInputs() {
        const variantId = this.querySelector(
          `#product-form-${this.dataset.section} input[name="id"], #product-form-installment-${this.dataset.section} input[name="id"], form[action*="/cart/add"] input[name="id"]`
        )?.value;

        if (!variantId) return null;

        return this.getProductVariants().find((variant) => String(variant.id) === String(variantId)) || null;
      }

      handleDynamicVariantInputChange(event) {
        if (!event.target.matches('input[name="id"], select[name^="options"], input[name^="options"]')) return;

        requestAnimationFrame(() => {
          const variant = this.getVariantFromInputs() || this.currentVariant;
          if (variant) {
            this.currentVariant = variant;
            this.updateDynamicVariantContent(variant);
          }
        });
      }

      getVariantOptionMap(variant) {
        const optionMap = new Map();

        this.getVariantOptions(variant).forEach(({ name, value }) => {
          optionMap.set(name, value);
          optionMap.set(this.normalizeDynamicLabel(name), value);
        });

        return optionMap;
      }

      buildDynamicProductTitle(variant) {
        const baseTitle = this.dataset.productTitle?.trim();
        if (!baseTitle) return '';

        const optionValues = this.getVariantOptions(variant).map(({ value }) => value).filter(Boolean);
        return [baseTitle, ...optionValues].join(' - ');
      }

      normalizeDynamicLabel(value) {
        return String(value || '')
          .trim()
          .replace(/[:\uFF1A]+$/g, '')
          .replace(/\s+/g, ' ')
          .toLocaleLowerCase();
      }

      escapeRegex(value) {
        return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      }

      updateDynamicVariantContent(variant) {
        if (!variant) return;

        const title = this.buildDynamicProductTitle(variant);
        if (title) {
          this.querySelectorAll('[data-dynamic-product-title]').forEach((element) => {
            element.textContent = title;
          });
        }

        const replacements = this.getVariantOptions(variant).map(({ name, value }) => ({ label: name, value }));
        if (variant.sku) {
          ['SKU', 'Product code', 'Productcode', 'Product Code', 'Artikelnummer', 'Artikel nr', 'Code', 'Urun Kodu', '\u00dcr\u00fcn Kodu'].forEach(
            (label) => replacements.push({ label, value: variant.sku })
          );
        }
        if (variant.barcode) {
          ['EAN', 'Barcode', 'Streepjescode'].forEach((label) => replacements.push({ label, value: variant.barcode }));
        }

        this.querySelectorAll('[data-dynamic-product-content]').forEach((element) => {
          if (!element.dataset.originalDynamicHtml) {
            element.dataset.originalDynamicHtml = element.innerHTML;
          }

          element.innerHTML = element.dataset.originalDynamicHtml;
          this.updateAdjacentDynamicValues(element, replacements);
          this.updateInlineDynamicValues(element, replacements);
        });

        this.updateMarkedDynamicValues(variant);
      }

      updateMarkedDynamicValues(variant) {
        const optionMap = this.getVariantOptionMap(variant);
        const dynamicValues = {
          sku: variant.sku || '',
          productcode: variant.sku || '',
          product_code: variant.sku || '',
          article_number: variant.sku || '',
          artikelnummer: variant.sku || '',
          barcode: variant.barcode || '',
          ean: variant.barcode || '',
        };

        this.querySelectorAll('[data-dynamic-value]').forEach((element) => {
          const key = this.normalizeDynamicLabel(element.dataset.dynamicValue).replace(/\s+/g, '_');
          element.textContent = dynamicValues[key] ?? '';
        });

        this.querySelectorAll('[data-dynamic-option]').forEach((element) => {
          const optionName = element.dataset.dynamicOption;
          const optionValue = optionMap.get(optionName) ?? optionMap.get(this.normalizeDynamicLabel(optionName));

          if (optionValue) {
            element.textContent = optionValue;
          }
        });
      }

      updateAdjacentDynamicValues(root, replacements) {
        const replacementMap = new Map(
          replacements.map(({ label, value }) => [this.normalizeDynamicLabel(label), value])
        );

        root.querySelectorAll('th, td, dt, dd, strong, b, span, p, li, .spec-k').forEach((element) => {
          const key = this.normalizeDynamicLabel(element.textContent);
          const value = replacementMap.get(key);
          if (!value) return;

          const target = element.matches('dd') ? element : element.nextElementSibling;
          if (!target || target === element) return;

          target.textContent = value;
        });
      }

      updateInlineDynamicValues(root, replacements) {
        const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
        const textNodes = [];

        while (walker.nextNode()) {
          textNodes.push(walker.currentNode);
        }

        textNodes.forEach((node) => {
          let text = node.nodeValue;

          replacements.forEach(({ label, value }) => {
            if (!label || !value) return;

            const expression = new RegExp(`(${this.escapeRegex(label)}\\s*[:\\uFF1A-]\\s*)([^\\n\\r|;/]+)`, 'gi');
            text = text.replace(expression, `$1${value}`);
          });

          node.nodeValue = text;
        });
      }

      toggleAddButton(disable = true, text, modifyClass = true) {
        const productForm = document.getElementById(`product-form-${this.dataset.section}`);
        if (!productForm) return;
        const addButton = productForm.querySelector('[name="add"]');
        const addButtonText = productForm.querySelector('[name="add"] > span');
        if (!addButton) return;

        if (disable) {
          addButton.setAttribute('disabled', 'disabled');
          if (text) addButtonText.textContent = text;
        } else {
          addButton.removeAttribute('disabled');
          addButtonText.innerHTML = FoxTheme.variantStrings.addToCart;
        }

        if (!modifyClass) return;
      }

      setUnavailable() {
        this.toggleAddButton(true, FoxTheme.variantStrings.unavailable);
        const price = document.getElementById(`price-${this.dataset.section}`);
        const inventory = document.getElementById(`Inventory-${this.dataset.section}`);
        const sku = document.getElementById(`Sku-${this.dataset.section}`);

        if (price) price.classList.add('hidden');
        if (inventory) inventory.classList.add('hidden');
        if (sku) sku.classList.add('hidden');
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.setQuantityBoundries();
        if (!this.hasAttribute('data-original-section')) {
          this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
            FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this)
          );
        }
      }

      setQuantityBoundries() {
        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityBoundries, {
          data: {
            sectionId: this.sectionId,
            productId: this.productId,
          },
        });
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.productIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart')?.classList.add('btn--loading');

        fetch(`${this.getAttribute('data-url')}?variant=${currentVariantId}&section_id=${this.sectionId}`)
          .then((response) => response.text())
          .then((responseText) => {
            const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.sectionId, this.productId, parsedHTML);
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            this.querySelector('.quantity__rules-cart')?.classList.remove('btn--loading');
          });
      }

      updateQuantityRules(sectionId, productId, parsedHTML) {
        if (!this.quantityInput) return;

        FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityRules, {
          data: {
            sectionId,
            productId,
            parsedHTML,
          },
        });

        this.setQuantityBoundries();
      }
    }
  );
}

if (!customElements.get('product-promotion-alert')) {
  customElements.define(
    'product-promotion-alert',
    class ProductPromotionAlert extends HTMLElement {
      constructor() {
        super();

        this.abortController = new AbortController();
        this.buttonEl = this.querySelector('button');
        this.buttonEl.addEventListener('click', this.onClick.bind(this), {
          signal: this.abortController.signal,
        });
      }

      onClick(evt) {
        evt.preventDefault();

        this.closest('.product__block').classList.add('hidden');
      }

      disconnectedCallback() {
        this.abortController.abort();
      }
    }
  );
}
